// src/pages/AuditPage.tsx
//
// ⚠ THE SENTENCES ON THIS PAGE ARE PART OF THE COMPLIANCE SURFACE.
//
// The outcome filter runs in the BROWSER, over the capped window `?limit=`
// fetched from the server — `/api/audit` has no `outcome` parameter, so there
// is nothing to push down to it. That is fine as a filter and was NOT fine as a
// sentence: the empty state read `No events have outcome "DENIED".`, stated
// flatly, about a trail whose whole purpose is that a denial leaves a trace.
// The true claim is only ever about the loaded window, and a compliance
// reviewer is exactly the reader who must never be told the stronger one.
//
// Sorting, by contrast, IS server-side (`AUDIT_SORT_COLUMNS`), so a sorted
// window is a genuine top-N by that column rather than a reshuffle of whichever
// rows happened to arrive.
import { plainCode } from '../lib/plain-words'
import { useEffect, useId, useState } from 'react'
import { useSearchParams } from 'react-router'
import { SgdsButton, SgdsInput, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { ActionBadge, AuditOutcomeCell, CategoryBadge, RoleBadge } from '../components/StatusBadge'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { ServiceDatePicker } from '../components/ServiceDatePicker'
import { formatInstant } from '../lib/format'
import { targetValue } from '../lib/sgds-dom'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'
import {
  AUDIT_OUTCOMES,
  AUDIT_OUTCOME_LABELS,
  AUDIT_OUTCOME_MEANING,
  AUDIT_REASON_LABELS,
  AUDIT_REASON_MEANING,
  summariseGrounds,
  type AuditOutcome,
} from '../lib/audit-outcome'

const ALL = 'ALL'

/**
 * ⚠ Derived from `AUDIT_OUTCOMES`, never listed again here.
 *
 * The literal this replaces was `['ALLOWED', 'DENIED', 'ERROR']`, and it is the
 * third place migration 009's fourth value had to be added by hand — the union
 * in `types.ts` and the two `Record`s in `StatusBadge.tsx` being the others.
 * None of the three failed to compile, because the API value is cast. Reading
 * the one array means the next widening lands here for free.
 */
const OUTCOMES: readonly AuditOutcome[] = AUDIT_OUTCOMES

/**
 * ★★ WAVE 9 — story 15's severity levels, as the SERVER's own vocabulary.
 *
 * ⚠ Three values, mirroring `AUDIT_SEVERITIES` in `services/audit/src/audit-severity.ts`,
 * and the route REFUSES anything else with a 400 naming the three. A fourth value
 * invented here would be a 400 this screen had invited — the same reason the
 * agency field is rendered only where the reference catalogue says a parameter
 * has one.
 *
 * ⚠ Severity is DERIVED from the outcome the trail already stores; it is not a
 * second, independently writable classification. That is what makes it safe for a
 * non-privileged caller: the derivation runs on the ALREADY-PROJECTED outcome, so
 * a 404-resolving row a caller was shown as `REFUSED` classifies as `MEDIUM` for
 * them and cannot leak the server's real decision (C6). `audit-severity.ts`'s own
 * header carries that argument.
 */
const SEVERITIES = ['HIGH', 'MEDIUM', 'LOW'] as const

const SEVERITY_MEANING: Readonly<Record<(typeof SEVERITIES)[number], string>> = {
  HIGH: 'Denied, and server errors',
  MEDIUM: 'Refused: state conflicts and domain refusals',
  LOW: 'Allowed',
}

/**
 * A calendar day, as the canonical UTC instant the route demands.
 *
 * ⚠ The route validates with `instant()` and refuses anything that is not
 * `YYYY-MM-DDTHH:MM:SS.sssZ`, so a bare `YYYY-MM-DD` from the date field would be
 * a 400 this screen invited. Both bounds are INCLUSIVE in the repository
 * (`AuditListFilter.occurredFrom`'s own docstring says so), which is why the upper
 * bound is the last millisecond of the chosen day and not its midnight: `to =
 * 2026-08-01` has to mean "including everything that happened on 1 August", or a
 * reviewer filtering a single day gets an empty table and no explanation.
 *
 * ⚠ UTC, never the viewer's zone. `occurred_at` is written by PostgreSQL's own
 * clock and every other date on this product is resolved in
 * `PRS2_OPERATING_TIME_ZONE` server-side; a browser-local day boundary here would
 * silently include or exclude up to sixteen hours of the trail depending on where
 * the officer is sitting.
 */
function startOfDayInstant(day: string): string {
  return `${day}T00:00:00.000Z`
}

function endOfDayInstant(day: string): string {
  return `${day}T23:59:59.999Z`
}

/** Mirrors `AUDIT_SORT_COLUMNS` in `packages/persistence/src/audit-repository.ts` exactly. */
type AuditSortKey = 'action' | 'outcome' | 'actorRole' | 'source' | 'requestId' | 'occurredAt'

const COLUMN_LABELS: Readonly<Record<AuditSortKey, string>> = {
  action: 'Action',
  outcome: 'Outcome',
  actorRole: 'Actor role',
  source: 'Source',
  requestId: 'Request id',
  occurredAt: 'When',
}

/**
 * What the loaded window actually IS, given the sort and the cap. Two facts
 * that both have to be said, because either alone misleads: which events the
 * server picked, and whether the window is full (and therefore probably hiding
 * more behind it).
 */
function windowSentence(column: AuditSortKey | null, dir: 'asc' | 'desc', loaded: number, limit: number): string {
  const picked =
    column === null
      ? `The window holds the ${loaded} most recent events this scope can see.`
      : `The window holds the ${loaded} events that sort ${dir === 'asc' ? 'first' : 'last'} by ${COLUMN_LABELS[column]}, not the most recent.`
  // `loaded >= limit` and not `===`: the server clamps rather than rejects, so
  // a hand-edited limit can come back under what was asked for, and treating
  // that as "full" is the conservative direction for a "there may be more"
  // warning.
  const capped = loaded >= limit ? ` It is full at ${limit}, so there are likely more events behind it.` : ''
  return `${picked}${capped}`
}

export function AuditPage() {
  const [limit, setLimit] = useState(50)
  const [outcomeFilter, setOutcomeFilter] = useState<string>(ALL)
  const { sort, onSort, interacted } = useSort<AuditSortKey>()

  /**
   * ★★ WAVE 9 — STORY 15's THREE MISSING CONTROLS, and they are SERVER-SIDE.
   *
   * `GET /api/audit` has accepted `occurredFrom`/`occurredTo` since 2026-08-18 and
   * `actorUserId`/`severity` since Wave 9; the repository has honoured all four
   * since Task 15 and `/api/audit/export` has passed them since. This screen said
   * so on its own face, in two sentences that are now deleted rather than softened:
   * *"the export's own date-range/user-account/severity filters have no control
   * here yet"* and *"Neither screen exposes the date-range or user-account filters
   * the API also accepts"*.
   *
   * ⚠ **These are a DIFFERENT KIND of control from the Outcome select above them,
   * and the hint text has to say which.** Outcome filters the events ALREADY
   * LOADED, in the browser, over the capped window — there is no `?outcome=`
   * parameter to push down. These four narrow what the SERVER returns, so they
   * search the whole trail. Two similar-looking selects meaning different things is
   * exactly the trap this page's own header was written about.
   *
   * ⚠ The filters are held as calendar DAYS and converted to instants at the
   * boundary, so what the officer chose and what the route requires are never the
   * same variable.
   */
  const [fromDay, setFromDay] = useState('')
  const [toDay, setToDay] = useState('')
  const [actorFilter, setActorFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState<string>(ALL)
  // ★★★ WAVE 12 (P6) — THE THREE AXES THE ROUTE HAS ALWAYS ACCEPTED AND NO
  // CONTROL EVER SENT. `GET /api/audit` and `GET /api/audit/export` both take
  // `?action=`, `?resourceType=` and `?resourceId=`, and `AuditListFilter` has
  // bound all three since Task 15 — so P2C1 §12.142's *"search using  // user-assigned parameters"* was answerable on three more axes than any screen
  // offered. The date-range, user-account and severity axes were built in Wave 9;
  // these are the ones that were left, and they are the ones an investigation  // actually starts from: *what happened to THIS bill*.
  // ★ Seeded ONCE from the address (`?resourceType=&resourceId=&action=`), which is how the "Open in the audit
  // trail" links on the MSO screens land here already narrowed to one record. Read at mount only: after that the
  // controls own the value, so typing never fights the address bar.
  const [urlParams] = useSearchParams()
  const [actionFilter, setActionFilter] = useState(() => urlParams.get('action') ?? '')
  const [resourceTypeFilter, setResourceTypeFilter] = useState(() => urlParams.get('resourceType') ?? '')
  const [resourceIdFilter, setResourceIdFilter] = useState(() => urlParams.get('resourceId') ?? '')
  const actorFieldId = useId()
  const actionFieldId = useId()
  const resourceTypeFieldId = useId()
  const resourceIdFieldId = useId()
  const fromFieldId = useId()
  const toFieldId = useId()

  /**
   * The four server-side filters as the ROUTE's own parameter names — built once
   * and used by BOTH the list fetch and the export URL.
   *
   * ⚠ One object, deliberately. Story 15 asks to *"export exactly what is on it"*,
   * and a **Download** button that assembled its own query beside three populated
   * filter fields would produce a file of the whole trail while looking like a
   * download of the table — the widening-under-a-narrow-label shape this
   * repository records as its worst failure mode. There is one statement of what
   * is being asked for.
   */
  const serverFilters = {
    ...(fromDay.length > 0 && { occurredFrom: startOfDayInstant(fromDay) }),
    ...(toDay.length > 0 && { occurredTo: endOfDayInstant(toDay) }),
    ...(actorFilter.trim().length > 0 && { actorUserId: actorFilter.trim() }),
    ...(severityFilter !== ALL && { severity: severityFilter }),
    // ★★★ WAVE 12 (P6) — trimmed, and absent when empty rather than sent as ''.
    // An empty `?action=` would be an EXACT match against the empty string and
    // return nothing, which reads as "no events" rather than "no filter".
    ...(actionFilter.trim().length > 0 && { action: actionFilter.trim() }),
    ...(resourceTypeFilter.trim().length > 0 && { resourceType: resourceTypeFilter.trim() }),
    ...(resourceIdFilter.trim().length > 0 && { resourceId: resourceIdFilter.trim() }),
  }
  const filterCount = Object.keys(serverFilters).length

  /**
   * ★★ PAGING THE TRAIL — 2026-08-18, and two things here are load-bearing.
   *
   * ⚠ **The pin.** Reading the audit trail WRITES to the audit trail
   * (`audit-hook.ts` records every request, this one included), so on a
   * newest-first list each page fetch inserts a row at the head and shifts every
   * later page by one. An unpinned seven-row walk was measured returning five
   * ids twice. The server reports `asOf`; the FIRST page's value is captured
   * here and sent back as `asOf` on every page after it, which closes the
   * population. Reset whenever the sort or the page size changes, because the
   * anchor belongs to the walk, not to the screen.
   *
   * ⚠ **It was sent back as `occurredTo` until S-075, and that was TWO defects
   * in one line.** It OVERWROTE the officer's own upper bound, so page two of a
   * date-filtered trail was served from a population bounded at `asOf` instead —
   * an officer who filtered to July saw August rows the moment they pressed
   * Next, and the filter chips still said July. And `?occurredTo=` is inclusive
   * of the whole millisecond it names, which is one millisecond too wide for an
   * anchor. `?asOf=` is a separate parameter applied exactly, so the pin and the
   * filter now AND together and the population really is `min(filterTo, asOf)`
   * that the old comment here claimed it was.
   *
   * ⚠ **`limit + 1`.** The trail reports no total — see `AuditListResponse` —
   * so the only way to know whether a next page exists is to ask for one row
   * more than is displayed and see whether it arrives. The extra row is dropped
   * before rendering; it is a probe, not content.
   */
  const [anchor, setAnchor] = useState<string | undefined>(undefined)
  const [offset, setOffset] = useState(0)
  const audit = useApi(
    () =>
      api.audit({
        limit: limit + 1,
        ...(offset > 0 && { offset }),
        ...serverFilters,
        // ⚠ THE PIN AND THE FILTER ARE DIFFERENT PARAMETERS, so they COMPOSE
        // rather than one clobbering the other. The walk's population is
        // genuinely `min(filterTo, asOf)`: `asOf` is when page one was served, so
        // nothing after it belongs to this walk whatever the officer typed, and
        // the officer's own `to` bound still applies on every page.
        //
        // ⚠ This line used to be `{ occurredTo: anchor }` spread after
        // `serverFilters` specifically SO THAT it would overwrite the officer's
        // bound — which silently widened page two of every date-filtered walk.
        // See the block above; it is why `?asOf=` exists.
        ...(offset > 0 && anchor !== undefined && { asOf: anchor }),
        ...sortParams(sort),
      }),
    // ⚠★ WAVE 12 (P6) — the three new filters ARE in this list, and the first draft
    // left them out. The query object carried them and nothing re-fetched, so a
    // filter took effect only on the next unrelated reload: accepted and
    // discarded, which this route's own comments call worse than one refused.
    // `AuditPage.dom.test.tsx` caught it.
    /*
      ⚠★★★ WAVE 25 — `anchor` IS NOT IN THIS LIST, and it used to be. That
      membership was a fetch storm on every live gateway: the capture effect
      below set `anchor` from each page-one response's `asOf`, the dep change
      re-fired this fetch, the fresh response carried a fresh `asOf` — the serve
      time, moved further by the audited read's OWN row — and the loop ran at
      ~81 requests/second. Measured on the running demonstration: 2,036 requests
      in 25 seconds. Invisible to every DOM test, because the mock's `asOf` was
      a constant and `setAnchor(same value)` bails; only live data sustains it.

      Removing it is safe by construction: the pin is only SENT when
      `offset > 0`, and every legitimate anchor transition co-fires another
      member of this list — paging changes `offset`, and a filter/sort/limit
      change resets the walk through the effect below, whose own deps fire the
      fetch. The one transition that must NOT refetch is the capture itself,
      which is exactly the case removing it closes.
      `AuditPage.dom.test.tsx`'s moving-asOf cases pin both halves.
    */
    [
      limit,
      offset,
      sort.column,
      sort.dir,
      fromDay,
      toDay,
      actorFilter,
      severityFilter,
      actionFilter,
      resourceTypeFilter,
      resourceIdFilter,
    ],
  )

  // Capture the anchor from page one, and only from page one: re-reading it
  // later would re-open the population the pin exists to close.
  //
  // ⚠★ WAVE 25 — `anchor === undefined` is the ONCE. Unguarded, this ran on
  // every page-one response and re-pinned the walk to each fresh `asOf`, which
  // both defeated the pin (the population re-opened per response — the exact
  // drift the comment above says it exists to close) and, with `anchor` then in
  // the fetch deps, drove the request storm recorded there. The walk is pinned
  // to the FIRST page-one response; a new walk gets a new pin through the reset
  // effect below, which puts `anchor` back to undefined.
  useEffect(() => {
    if (offset === 0 && anchor === undefined && audit.data?.asOf !== undefined) setAnchor(audit.data.asOf)
  }, [offset, anchor, audit.data?.asOf])

  // ⚠ Sort or page size changing means a DIFFERENT walk. Page three of one order
  // is not page three of another, and an anchor minted under the old walk would
  // silently bound the new one.
  //
  // ★★ WAVE 9 — the filters are in these deps as a BACKSTOP, not as the mechanism.
  // `applyFilter` resets the offset and the anchor in the same update as the filter
  // itself, which is what stops a request firing at the stale offset first (see its
  // own docstring — `AccessControlMatrixPage` paid for that defect already). They
  // are listed here so that a filter changed by some future path that forgets to
  // use `applyFilter` still cannot walk a new population at an old offset: a
  // redundant request is a bad outcome, and page three of a three-row result set is
  // a worse one.
  useEffect(() => {
    setOffset(0)
    setAnchor(undefined)
    // ⚠ The anchor belongs to a WALK over one population, so every filter that
    // changes the population has to reset it — including the three added in P6.
  }, [
    sort.column,
    sort.dir,
    limit,
    fromDay,
    toDay,
    actorFilter,
    severityFilter,
    actionFilter,
    resourceTypeFilter,
    resourceIdFilter,
  ])

  const fetched = audit.data?.events ?? []
  const hasMore = fetched.length > limit
  const events = hasMore ? fetched.slice(0, limit) : fetched
  const pager = useOffsetPager(limit, undefined, hasMore)

  // ⚠ The pager owns the page NUMBER; this component owns the offset it fetches
  // with, because the fetch is keyed on it. Kept in step in one place rather
  // than two sources of truth — S-011's shape in a component.
  useEffect(() => {
    if (pager.offset !== offset) setOffset(pager.offset)
  }, [pager.offset, offset])

  /**
   * ★★ CHANGE A FILTER AND RESET THE WALK IN THE SAME UPDATE — never in an effect.
   *
   * ⚠ **This is a defect `AccessControlMatrixPage` already paid for, and its own
   * test records the diagnosis:** *"the offsets used to be reset in an effect that
   * ran AFTER the fetcher, so applying a filter while off page one fired a request
   * at the STALE findingOffset first and the correct one second."* React batches all
   * of these into one render, so a filter changed through here never reaches the
   * fetcher paired with an old offset.
   *
   * ⚠ **`pager.reset()` IS REQUIRED and `setOffset(0)` alone is not enough** — found
   * by the test failing on the first attempt, which is the only reason it is
   * written down. The PAGER owns the page number and this component owns the offset
   * it fetches with; an effect below copies `pager.offset` into `offset` whenever
   * they disagree. So `setOffset(0)` on its own is undone on the very next render by
   * that effect copying the pager's unchanged 50 straight back. Both halves of the
   * one fact have to move together, which is what `useOffsetPager`'s own docstring
   * warns about when it calls a stored page number "two statements of one fact".
   *
   * ⚠ **And the cost of getting this wrong is higher on THIS route than on the
   * matrix.** Every request to this gateway WRITES a row into the table this screen
   * is reading (`audit-hook.ts` records each one in `onResponse`), so a wasted
   * request is not merely waste — it appends to the population the pin exists to
   * close, which is the mechanism `audit-volume.test.ts` was written about.
   * `useApi`'s `cancelled` guard would keep the stale ANSWER off the screen; nothing
   * would keep its audit row out of the trail.
   */
  function applyFilter(change: () => void): void {
    change()
    pager.reset()
    setOffset(0)
    setAnchor(undefined)
  }

  const filtered = outcomeFilter === ALL ? events : events.filter((e) => e.outcome === outcomeFilter)

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Audit trail</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Every request PRS2.0 received, with who, what and when. A denial that left no
          trace is exactly what this trail exists to prevent, and a denial buried among ordinary refusals is
          the same failure by a slower route, which is why the outcome is recorded four ways and each row
          also carries the grounds.
        </p>
      </div>

      {/*
        `.pt-controlbar`, not `flex items-end`. See that class's block comment in
        styles/index.css for the measurements: the Outcome select is 28px taller
        than the Show select purely because it carries hint text, and
        bottom-aligning let that one cell drag both labels, both control boxes
        and the button onto three separate lines.
      */}
      <div className="pt-card pt-controlbar">
        <SgdsSelect
          label="Outcome"
          hintText="Narrows the events already loaded below, not a search of the whole trail."
          value={outcomeFilter}
          onSgdsChange={(e) => setOutcomeFilter(targetValue(e))}
        >
          <SgdsSelectOption value={ALL}>All outcomes</SgdsSelectOption>
          {OUTCOMES.map((outcome) => (
            <SgdsSelectOption key={outcome} value={outcome}>
              {AUDIT_OUTCOME_LABELS[outcome]}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
        <SgdsSelect
          label="Show"
          hintText="How many of the most recent events to fetch from the server."
          value={String(limit)}
          onSgdsChange={(e) => setLimit(Number(targetValue(e)))}
        >
          <SgdsSelectOption value="50">Last 50</SgdsSelectOption>
          <SgdsSelectOption value="100">Last 100</SgdsSelectOption>
          <SgdsSelectOption value="200">Last 200</SgdsSelectOption>
        </SgdsSelect>
        {/*
          ★★ WAVE 9 — STORY 15's THREE MISSING CONTROLS. Every one of these
          narrows what the SERVER returns, which is what makes them different from
          the Outcome select above; each hint text says so, because two
          similar-looking selects that mean "search the trail" and "filter what is
          already here" is the trap this page's header was written about.
        */}
        <div className="pt-controlbar-field">
          <span id={fromFieldId} className="block text-sm font-semibold mb-1">
            From
          </span>
          <ServiceDatePicker value={fromDay} onChange={(day) => applyFilter(() => setFromDay(day))} labelledBy={fromFieldId} />
          <p className="mt-1 text-xs text-[var(--sgds-body-color-subtle)]">
            Searches the whole trail from this day, inclusive. UTC.
          </p>
        </div>
        <div className="pt-controlbar-field">
          <span id={toFieldId} className="block text-sm font-semibold mb-1">
            To
          </span>
          <ServiceDatePicker value={toDay} onChange={(day) => applyFilter(() => setToDay(day))} labelledBy={toFieldId} />
          <p className="mt-1 text-xs text-[var(--sgds-body-color-subtle)]">
            Inclusive: the whole of this day is included.
          </p>
        </div>
        <SgdsInput placeholder=""
          label="User account"
          id={actorFieldId}
          hintText="The actor's account id. Searches the whole trail, not the loaded window."
          value={actorFilter}
          onSgdsInput={(e) => applyFilter(() => setActorFilter(targetValue(e)))}
        />
        {/*
          ★★★ WAVE 12 (P6) — P2C1 §12.142's REMAINING AXES. The route and the export
          have accepted all three since Task 15 and no control ever sent them, so the
          clause's "search using user-assigned parameters" was answerable on three
          more axes than any screen offered.

          ⚠ These are the axes an INVESTIGATION starts from. "What happened to this
          bill" is resource type + resource id; "show me every rejection" is action.
          The Wave 9 four answer "when, by whom, how serious" — useful for a sweep,
          and not what a reviewer opens the trail to ask about one bill.

          ⚠ EXACT matches, and the hints say so. The repository compares with `=`,
          not `LIKE`, so a partial action returns nothing — and a filter that
          silently matched nothing would read as "this never happened", which on a
          compliance surface is the worst available answer. Examples are given
          rather than described, because an exact-match field with no example is a
          guessing game.
        */}
        <SgdsInput placeholder=""
          label="Action"
          id={actionFieldId}
          hintText="Exact match on the action name as shown in the technical record. Asks the server, not the loaded window."
          value={actionFilter}
          onSgdsInput={(e) => applyFilter(() => setActionFilter(targetValue(e)))}
        />
        <SgdsInput placeholder=""
          label="Resource type"
          id={resourceTypeFieldId}
          hintText="Exact match, e.g. bill, officer, inbound-file. Pair it with a resource id to follow one thing."
          value={resourceTypeFilter}
          onSgdsInput={(e) => applyFilter(() => setResourceTypeFilter(targetValue(e)))}
        />
        <SgdsInput placeholder=""
          label="Resource id"
          id={resourceIdFieldId}
          hintText="Exact match, e.g. a bill reference. This is how you read everything that happened to one bill."
          value={resourceIdFilter}
          onSgdsInput={(e) => applyFilter(() => setResourceIdFilter(targetValue(e)))}
        />
        <SgdsSelect
          label="Severity"
          hintText="Derived from the outcome. Asks the SERVER for only these events."
          value={severityFilter}
          onSgdsChange={(e) => applyFilter(() => setSeverityFilter(targetValue(e)))}
        >
          <SgdsSelectOption value={ALL}>All severities</SgdsSelectOption>
          {SEVERITIES.map((severity) => (
            <SgdsSelectOption key={severity} value={severity}>
              {severity}: {SEVERITY_MEANING[severity]}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
        {/* `outline`, not `ghost`: it stands in a row of bordered fields, and a
            bare text link among them reads as prose rather than as something to
            press. `--end` separates a re-fetch from the two filters it is not
            part of; the `.pt-controlbar-action` wrapper is what drops it onto
            the control line instead of the label line. */}
        <div className="pt-controlbar-action pt-controlbar-action--end">
          {/*
            ★ TASK 15 — P2C1 §12.52's download half, for this screen. A plain
            navigation, not a fetch: `GET /api/audit/export` answers with an
            `attachment` Content-Disposition, so the browser downloads the
            file and stays on this page — no blob handling needed.

            ★★ WAVE 9 — AND IT CARRIES THE SCREEN'S OWN FILTERS. This comment
            read: *"Carries the screen's CURRENT sort order only; the export's
            own date-range/user-account/severity filters have no control here
            yet (see RTM.md's Annex A 15 row) and are reachable only via the
            API directly."* The controls exist now, and a **Download** button
            standing beside four populated filter fields while producing a file
            of the whole trail would be worse than the gap it replaced — story
            15's own words are "export exactly what is on it". `serverFilters`
            is the ONE statement of what is being asked for; both the table
            above and these two files read it.

            ⚠ What the file still will NOT match exactly, said here because a
            reviewer will compare them: the OUTCOME select filters in the
            browser over the loaded window and has no server parameter, so a
            table narrowed to DENIED exports as its severity band rather than
            that one outcome. And the export is bounded at
            MAX_AUDIT_EXPORT_ROWS and REFUSES past it rather than truncating,
            so a wide filter can download nothing while the table shows its
            first page.
          */}
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onRefresh={audit.reload}
            onDownload={(format) => {
              window.location.href = api.auditExportUrl(format, { ...sortParams(sort), ...serverFilters })
            }}
            email={
              <EmailReportButton
                path="/api/audit/export"
                query={{ ...sortParams(sort), ...serverFilters } as Record<string, string>}
              />
            }
          />
          {/*
            ★ Clear, and it is rendered ONLY when there is something to clear —
            four fields and a select is enough state that an officer who has
            narrowed to an empty table needs one action to get back, rather than
            emptying each field and remembering which ones they touched.
          */}
          {filterCount > 0 && (
            <SgdsButton
              variant="outline"
              type="button"
              onClick={() =>
                applyFilter(() => {
                  setFromDay('')
                  setToDay('')
                  setActorFilter('')
                  setSeverityFilter(ALL)
                })
              }
            >
              Clear {filterCount} filter{filterCount === 1 ? '' : 's'}
            </SgdsButton>
          )}
        </div>
      </div>

      {/*
        ★ TASK 16 — Annex A stories 16/17. Plain navigations, same shape as
        the audit-export buttons above. The access-control matrix answers 403
        (JSON, not a file) for a role that does not hold the full-trail
        capability — surfaced as a failed download rather than fetched and
        checked, matching every other export link on this page.
      */}
      <div className="pt-card">
        <h2 className="text-lg font-semibold mb-1">Administrative reports</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-4 max-w-[70ch]">
          The user authentication report, the access control matrix and the
          per-officer approval activity report: all
          reachable only to a role holding this session&rsquo;s own full-audit-trail capability. ⚠ These two
          take the whole population and are <strong>not</strong> narrowed by the filters above: those
          belong to the audit trail on this page, and the authentication report accepts its own
          date-range and user-account parameters that have no control here. The access-control matrix
          has its own screen at Administration &rarr; Access control, where its filters do.
        </p>
        <div className="pt-controlbar-action">
          <SgdsButton variant="outline" type="button" onClick={() => { window.location.href = api.authenticationReportUrl('pdf') }}>
            Authentication report (PDF)
          </SgdsButton>
          <SgdsButton variant="outline" type="button" onClick={() => { window.location.href = api.authenticationReportUrl('xlsx') }}>
            Authentication report (Excel)
          </SgdsButton>
          <SgdsButton variant="outline" type="button" onClick={() => { window.location.href = api.accessControlMatrixUrl('pdf') }}>
            Access control matrix (PDF)
          </SgdsButton>
          <SgdsButton variant="outline" type="button" onClick={() => { window.location.href = api.accessControlMatrixUrl('xlsx') }}>
            Access control matrix (Excel)
          </SgdsButton>
          {/*
            ★★ WAVE 9 — Annex A story 37's fourth limb. Same shape as the two above:
            a plain navigation, and a 403 for a role without the full-trail
            capability surfaces as a failed download.

            ⚠ The refusal is NOT a narrowing, and the route says so in its own
            message: a per-officer report names individuals, so the officer IS the
            grouping key and there is no smaller version to serve.
          */}
          <SgdsButton variant="outline" type="button" onClick={() => { window.location.href = api.approvalActivityReportUrl('pdf') }}>
            Approval activity by officer (PDF)
          </SgdsButton>
          <SgdsButton variant="outline" type="button" onClick={() => { window.location.href = api.approvalActivityReportUrl('xlsx') }}>
            Approval activity by officer (Excel)
          </SgdsButton>
        </div>
      </div>

      <OutcomeLegend events={events} />

      <SortAnnouncement sort={sort} interacted={interacted} busy={audit.loading} columnLabels={COLUMN_LABELS} rowCount={filtered.length} />

      {audit.error !== null ? (
        <ErrorCard title="Could not load the audit trail" message={audit.error} onRetry={audit.reload} />
      ) : audit.initialLoading ? (
        <LoadingCard label="Loading audit trail" rows={8} />
      ) : filtered.length === 0 ? (
        <EmptyCard
          title={events.length === 0 ? 'No audit events loaded' : 'No loaded events match this filter'}
          /*
            ⚠ Both sentences are scoped to the LOADED WINDOW, and neither makes
            a claim about the trail. The one they replace — `No events have
            outcome "DENIED".` — is a statement about the compliance record that
            this page cannot support: the filter only ever sees the `?limit=`
            events fetched, and a denial outside that window would produce the
            same empty card and the same false sentence.
          */
          message={
            events.length === 0
              ? `Nothing came back for this window. It requests the ${limit} most recent events your scope can read, so either nothing has happened yet or nothing in it is visible to your role.`
              : `None of the ${events.length} events loaded here are "${AUDIT_OUTCOME_LABELS[outcomeFilter as AuditOutcome] ?? outcomeFilter}". This filter searches only what is loaded: it is not a search of the whole trail. Widen "Show", or sort by Outcome to bring that outcome's events into the window.`
          }
          {...(events.length > 0 ? { onClear: () => setOutcomeFilter(ALL) } : {})}
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          {/* Stays mounted through a re-sort so the activated header button —
              and the keyboard focus on it — survives the round trip. */}
          <div className="pt-table-wrap" aria-busy={audit.loading}>
            <table className="pt-table">
              <caption className="sr-only">
                Audit events: the {limit} most recent your scope can read
                {outcomeFilter !== ALL ? `, narrowed to outcome "${AUDIT_OUTCOME_LABELS[outcomeFilter as AuditOutcome] ?? outcomeFilter}"` : ''}
              </caption>
              <thead>
                <tr>
                  <SortableTh label="Action" sortKey="action" sort={sort} onSort={onSort} />
                  <SortableTh label="Outcome" sortKey="outcome" sort={sort} onSort={onSort} />
                  <SortableTh label="Actor role" sortKey="actorRole" sort={sort} onSort={onSort} />
                  <SortableTh label="Source" sortKey="source" sort={sort} onSort={onSort} />
                  <SortableTh label="Request id" sortKey="requestId" sort={sort} onSort={onSort} />
                  <SortableTh label="When" sortKey="occurredAt" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((event) => (
                  <tr key={event.id}>
                    <td><ActionBadge action={event.action} /></td>
                    <td>
                      <AuditOutcomeCell outcome={event.outcome} {...(event.detail !== undefined && { detail: event.detail })} />
                    </td>
                    <td><RoleBadge role={event.actorRole} /></td>
                    <td><CategoryBadge kind="source" value={event.source} label={plainCode(event.source)} /></td>
                    <td className="pt-mono text-xs">{event.requestId}</td>
                    <td className="pt-tabular">{formatInstant(event.occurredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)]">
            Showing {filtered.length} of {events.length} event{events.length === 1 ? '' : 's'}
            {outcomeFilter !== ALL ? `: filtered to "${AUDIT_OUTCOME_LABELS[outcomeFilter as AuditOutcome] ?? outcomeFilter}"` : ''}.{' '}
            {windowSentence(sort.column, sort.dir, events.length, limit)}
          </p>
          {/*
            ★★ THE PAGER — 2026-08-18. Before it, this screen showed the forty
            most recent events of the trail and no others, for ever, while
            `windowSentence` above explained that a window existed. Explaining a
            wall is not the same as opening a door.

            ⚠ `total={undefined}` is deliberate and is not missing data: the
            trail reports no total (a filtered `count(*)` over the largest
            append-only table in the product, on every page view, is what P2C14
            §6.1.1's 1 s p95 does not survive), so the control says "page 3"
            and offers next/previous rather than inventing "of 9".
          */}
          <Pagination pager={pager} label="audit events" total={undefined} shown={events.length} />
        </div>
      )}
    </div>
  )
}

/**
 * What the four outcomes mean, and what grounds actually occur in the window on
 * screen.
 *
 * ⚠ The reason vocabulary is not self-explanatory, and two of its values are
 * the point of the whole classification: `scope` (the record is real and you may
 * not see it) and `absent` (there is no such record) are indistinguishable on
 * the wire ON PURPOSE — identical 404s, identical bodies, so the API cannot be
 * used to enumerate. That makes this ledger the only place they can be told
 * apart, and a reader who does not know the vocabulary cannot tell them apart
 * here either.
 *
 * Only the grounds PRESENT in the loaded window are listed. A static legend of
 * all ten would be a page of vocabulary the reader mostly does not need, and —
 * worse on this particular surface — it would imply the trail contains kinds of
 * event that it does not.
 */
function OutcomeLegend({ events }: { events: readonly { outcome: string; detail?: Record<string, unknown> }[] }) {
  // ⚠ `summariseGrounds`, not a walk written out here. This loop used to test
  // `isDisclosureWithheld` and `continue` before reading the reason — the right
  // order, undefended: a withheld row also has no readable reason, so moving the
  // `continue` would have counted every redaction as "grounds not recorded" and
  // reported a working control (S-014) as a hole in the compliance trail. The
  // per-row cell in `StatusBadge.tsx` had the same ordering in a ternary. Both
  // now read one tested function, so there is one place to get it right.
  const { grounds, withheld } = summariseGrounds(events)

  return (
    <details className="pt-card">
      <summary className="cursor-pointer font-semibold">
        What the outcomes mean{grounds.length > 0 ? `, and the ${grounds.length === 1 ? 'ground' : `${grounds.length} grounds`} present below` : ''}
      </summary>

      {/*
        Two definition lists in a gapped column, with ONE `dt` width shared
        between them — the two blocks are read as one glossary, and a second
        indent would make them look like unrelated tables. Spacing comes from
        the container's `gap`, never from a margin on a child, so this layout
        does not depend on the utility-vs-base cascade at all.
      */}
      <div className="mt-4 flex flex-col gap-6 max-w-[80ch]">
        <dl className="flex flex-col gap-3">
          {AUDIT_OUTCOMES.map((outcome) => (
            <div key={outcome} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
              <dt className="sm:w-56 shrink-0 font-semibold text-sm">{AUDIT_OUTCOME_LABELS[outcome]}</dt>
              <dd className="text-sm text-[var(--sgds-body-color-subtle)]">{AUDIT_OUTCOME_MEANING[outcome]}</dd>
            </div>
          ))}
        </dl>

        {grounds.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-[var(--sgds-border-color-muted)] pt-5">
            <h3 className="text-sm font-semibold">Grounds recorded in the events loaded below</h3>
            <dl className="flex flex-col gap-3">
              {/* No `as keyof typeof` casts any more: `summariseGrounds` returns
                  `AuditOutcomeReason`, already validated by `outcomeReasonOf`,
                  so both lookups are total by type rather than by hope. */}
              {grounds.map(({ reason, count }) => (
                <div key={reason} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                  <dt className="sm:w-56 shrink-0 font-semibold text-sm">
                    {AUDIT_REASON_LABELS[reason]}{' '}
                    <span className="pt-tabular font-normal text-[var(--sgds-color-subtle)]">({count})</span>
                  </dt>
                  <dd className="text-sm text-[var(--sgds-body-color-subtle)]">{AUDIT_REASON_MEANING[reason]}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/*
          Stated only when such rows are actually loaded, like the grounds block
          above — and stated as a control that is working, never as a gap. The
          server records which of the two a 404 really was and redacts it back
          out for a reader who is not entitled to the full trail (S-014),
          because that answer is exactly what the 404 exists to withhold.
        */}
        {withheld > 0 && (
          <div className="flex flex-col gap-3 border-t border-[var(--sgds-border-color-muted)] pt-5">
            <h3 className="text-sm font-semibold">
              Withheld{' '}
              <span className="pt-tabular font-normal text-[var(--sgds-color-subtle)]">({withheld})</span>
            </h3>
            <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
              These events were refused with a 404, which this system returns both for a record outside your
              scope and for one that does not exist: deliberately indistinguishable, so that a refusal cannot
              be used to discover what exists. The trail records which it really was; that ground is not shown
              to you. It is recorded, and an auditor can see it.
            </p>
          </div>
        )}
      </div>
    </details>
  )
}
